import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RecaptchaVerifier, linkWithPhoneNumber } from 'firebase/auth';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, isFirebasePhoneAuthTestingMode, isFirebaseConfigured, firebaseConfigError } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import getFirebaseErrorMessage from '../utils/firebaseErrors';
import useBackgroundImagePreload from '../hooks/useBackgroundImagePreload';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
const DEFAULT_COUNTRY_CODE = '+91';
const PHONE_DIGITS_PATTERN = /^\d{10}$/;

const normalizePhoneDigits = (value) => value.replace(/\D/g, '');
const formatFirebaseError = (firebaseError) => {
  const friendlyMessage = getFirebaseErrorMessage(firebaseError);
  return firebaseError?.code ? `${friendlyMessage} (${firebaseError.code})` : friendlyMessage;
};

function VerifyPhone() {
  const { user, loading } = useAuth();
  const { userProfile, refreshUserProfile } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/dashboard';
  const backgroundImageSrc = '/registraion-bg.png';
  const isBackgroundLoaded = useBackgroundImagePreload(backgroundImageSrc);

  const initialPhoneDigits = useMemo(() => {
    if (userProfile?.phone) {
      return normalizePhoneDigits(userProfile.phone).slice(-10);
    }

    if (user?.phoneNumber?.startsWith(DEFAULT_COUNTRY_CODE)) {
      return user.phoneNumber.slice(DEFAULT_COUNTRY_CODE.length);
    }

    return '';
  }, [user?.phoneNumber, userProfile?.phone]);

  const [phoneDigits, setPhoneDigits] = useState(initialPhoneDigits);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [otpCode, setOtpCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRecaptchaSolved, setIsRecaptchaSolved] = useState(false);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);
  const isLocalDevelopmentHost =
    typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const clearRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (_) {}
    }

    if (typeof document !== 'undefined') {
      const recaptchaContainer = document.getElementById('phone-recaptcha-container');
      if (recaptchaContainer) {
        recaptchaContainer.innerHTML = '';
      }
    }

    recaptchaVerifierRef.current = null;
    recaptchaWidgetIdRef.current = null;
    setIsRecaptchaSolved(false);
  };

  const setupRecaptchaVerifier = async ({ forceNew = false } = {}) => {
    if (loading || !user || !isFirebaseConfigured || !auth) {
      return null;
    }

    if (forceNew) {
      clearRecaptchaVerifier();
    }

    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    auth.languageCode = 'en';
    const verifier = new RecaptchaVerifier(auth, 'phone-recaptcha-container', {
      size: isFirebasePhoneAuthTestingMode ? 'invisible' : 'normal',
      callback: () => {
        setError('');
        setIsRecaptchaSolved(true);
      },
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
        clearRecaptchaVerifier();
      }
    });
    recaptchaVerifierRef.current = verifier;

    try {
      recaptchaWidgetIdRef.current = await verifier.render();
      return verifier;
    } catch (renderError) {
      console.error('Failed to render phone reCAPTCHA:', renderError);
      setError(formatFirebaseError(renderError));
      clearRecaptchaVerifier();
      return null;
    }
  };

  useEffect(() => {
    setPhoneDigits(initialPhoneDigits);
  }, [initialPhoneDigits]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/signin', { replace: true });
      return;
    }

    if (!loading && user?.phoneNumber) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, navigate, redirectTo, user]);

  useEffect(() => {
    if (loading || !user || recaptchaVerifierRef.current) {
      return undefined;
    }

    void setupRecaptchaVerifier();

    return () => {
      clearRecaptchaVerifier();
    };
  }, [loading, user?.uid]);

  const buildE164PhoneNumber = () => `${countryCode}${phoneDigits}`;
  const canSendOtp =
    !sendingOtp &&
    !verifyingOtp &&
    (isFirebasePhoneAuthTestingMode || isRecaptchaSolved);

  const resetRecaptcha = () => {
    if (
      recaptchaWidgetIdRef.current !== null &&
      typeof window !== 'undefined' &&
      window.grecaptcha &&
      typeof window.grecaptcha.reset === 'function'
    ) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
  };

  const saveVerifiedProfile = async (firebasePhoneNumber) => {
    const token = await auth.currentUser?.getIdToken(true);
    const payload = {
      uid: auth.currentUser?.uid,
      fullName: userProfile?.fullName || user?.displayName || '',
      email: user?.email || userProfile?.email || '',
      phone: normalizePhoneDigits(userProfile?.phone || phoneDigits),
      city: userProfile?.city || '',
      gender: userProfile?.gender || '',
      qualification: userProfile?.qualification || '',
      provider: userProfile?.provider || (user?.providerData?.some((provider) => provider.providerId === 'google.com') ? 'google' : 'email'),
      requireCompleteProfile: Boolean(userProfile?.provider === 'google'),
      firebasePhoneNumber
    };

    await axios.post(`${API_BASE_URL}/api/save_user_profile`, payload, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!isFirebaseConfigured || !auth) {
      setError(firebaseConfigError || 'Firebase is not configured.');
      return;
    }

    if (!PHONE_DIGITS_PATTERN.test(phoneDigits)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    if (!auth.currentUser) {
      setError('Your session has expired. Please sign in again.');
      return;
    }

    const verifier = await setupRecaptchaVerifier();

    if (!verifier) {
      setError('reCAPTCHA is still loading. Please wait a moment and try again.');
      return;
    }

    if (!isFirebasePhoneAuthTestingMode && !isRecaptchaSolved) {
      document.getElementById('phone-recaptcha-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setError('Please complete the reCAPTCHA challenge first.');
      return;
    }

    try {
      setSendingOtp(true);
      const result = await linkWithPhoneNumber(auth.currentUser, buildE164PhoneNumber(), recaptchaVerifierRef.current);
      setConfirmationResult(result);
      setSuccessMessage(`OTP sent to ${buildE164PhoneNumber()}.`);
    } catch (otpError) {
      console.error('Failed to send phone OTP:', otpError);
      resetRecaptcha();
      clearRecaptchaVerifier();
      await setupRecaptchaVerifier({ forceNew: true });
      setError(formatFirebaseError(otpError));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!confirmationResult) {
      setError('Please request an OTP first.');
      return;
    }

    if (!/^\d{6}$/.test(otpCode)) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    try {
      setVerifyingOtp(true);
      await confirmationResult.confirm(otpCode);
      await auth.currentUser?.reload();
      const verifiedPhoneNumber = auth.currentUser?.phoneNumber || buildE164PhoneNumber();
      await saveVerifiedProfile(verifiedPhoneNumber);
      await refreshUserProfile(auth.currentUser);
      setSuccessMessage('Mobile number verified successfully.');
      navigate(redirectTo, { replace: true });
    } catch (verifyError) {
      console.error('Failed to verify phone OTP:', verifyError);
      setError(formatFirebaseError(verifyError));
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)]">
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-300 ease-out ${
          isBackgroundLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundImage: `url('${backgroundImageSrc}')` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(248,244,251,0.62),rgba(255,248,243,0.52))]" />

      <div className="relative flex min-h-screen items-center justify-center px-5 py-6 sm:px-6 sm:py-8">
        <div className="w-full max-w-md rounded-3xl bg-white/95 p-6 shadow-xl backdrop-blur-sm sm:p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Verify Mobile Number</h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Add your mobile number and verify it with OTP to keep your account secure.
            </p>
          </div>

          {error ? (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : null}

          {successMessage ? (
            <div className="mt-5 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {successMessage}
            </div>
          ) : null}

          <form onSubmit={handleSendOtp} className="mt-6 space-y-4">
            <div>
              <label htmlFor="verify-phone-number" className="mb-2 block text-sm font-medium text-slate-700">
                Mobile Number
              </label>
              <div className="flex overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 transition-all focus-within:border-[#9a51a0] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#9a51a0]/20">
                <input
                  type="text"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value.startsWith('+') ? event.target.value : `+${event.target.value.replace(/\D/g, '')}`)}
                  className="w-20 shrink-0 border-r border-slate-200 bg-transparent px-3 py-3 text-sm font-semibold text-slate-700 focus:outline-none"
                  aria-label="Country code"
                />
                <input
                  id="verify-phone-number"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phoneDigits}
                  onChange={(event) => setPhoneDigits(normalizePhoneDigits(event.target.value).slice(0, 10))}
                  placeholder="Enter 10 digit number"
                  className="w-full bg-transparent px-3.5 py-3 text-sm text-slate-900 focus:outline-none"
                />
              </div>
            </div>

            {!isFirebasePhoneAuthTestingMode ? (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div id="phone-recaptcha-container" className="flex justify-center" />
              </div>
            ) : (
              <div id="phone-recaptcha-container" />
            )}

            {!isFirebasePhoneAuthTestingMode ? (
              <p className="text-xs leading-6 text-slate-500">
                Complete the reCAPTCHA challenge first, then click Send OTP.
              </p>
            ) : null}

            <button
              type="submit"
              disabled={!canSendOtp}
              className="w-full rounded-2xl bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sendingOtp ? 'Sending OTP...' : confirmationResult ? 'Resend OTP' : 'Send OTP'}
            </button>
          </form>

          <form onSubmit={handleVerifyOtp} className="mt-5 space-y-4">
            <div>
              <label htmlFor="verify-otp-code" className="mb-2 block text-sm font-medium text-slate-700">
                OTP Code
              </label>
              <input
                id="verify-otp-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(event) => setOtpCode(normalizePhoneDigits(event.target.value).slice(0, 6))}
                placeholder="Enter 6 digit OTP"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-900 transition-all focus:border-[#9a51a0] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#9a51a0]/20"
              />
            </div>

            <button
              type="submit"
              disabled={!confirmationResult || sendingOtp || verifyingOtp}
              className="w-full rounded-2xl border border-[#eadffb] bg-white px-4 py-3 text-sm font-semibold text-[#7d57b1] shadow-sm transition-colors hover:bg-[#faf7ff] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>

          <p className="mt-5 text-xs leading-6 text-slate-500">
            Complete the reCAPTCHA challenge before sending OTP.
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyPhone;
